import { useEffect, useRef, useState } from "react";
import { serverNow } from "@/lib/api";

// PRIVATE RESPONSE WINDOWS (Protection Card after a weather result). Every player in the
// window gets a private panel of the same size, holder or
// not, so neither the pause nor a glance at a screen gives a card away.
//   online       everyone in the window answers at once on their own screen; other screens
//                (and players already answered / not in it) only see "Players are deciding…".
//   Pass & Play  one player at a time: a neutral cover "<name>: tap to see your response
//                (others look away)", then their private panel, then the next player.

// Seconds left until `deadlineIso`, on the server's clock.
export function useServerCountdown(deadlineIso) {
  const deadlineMs = deadlineIso ? new Date(deadlineIso).getTime() : 0;
  const calc = () => (deadlineMs ? Math.max(0, Math.ceil((deadlineMs - serverNow()) / 1000)) : 0);
  const [remaining, setRemaining] = useState(calc);
  const [total, setTotal] = useState(calc);
  useEffect(() => {
    setTotal(calc());
    if (!deadlineMs) return undefined;
    const tick = () => setRemaining(calc());
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [deadlineMs]); // eslint-disable-line react-hooks/exhaustive-deps
  return { remaining, total: Math.max(1, total), deadlineMs };
}

// Which panel this screen shows.
//   window: { responders, answers, options, current, deadline }
//   ctx:    { online, seat }  (seat = this screen's colour online, Pass & Play the "plays as" seat or null)
// -> { mode: "mine" | "cover" | "deciding", color, option, reveal() }
export function useResponseView(win, ctx) {
  const [revealedKey, setRevealedKey] = useState(null);
  const responders = win?.responders || [];
  const answers = win?.answers || {};
  const key = `${win?.current || ""}|${win?.deadline || ""}`;
  if (!win) return { mode: "deciding", color: null, option: false, reveal: () => {} };
  if (ctx.online) {
    const me = ctx.seat;
    const mine = !!me && responders.includes(me) && !answers[me];
    return { mode: mine ? "mine" : "deciding", color: mine ? me : null, option: mine && !!win.options?.[me], reveal: () => {} };
  }
  const cur = win.current;
  if (!cur || answers[cur] || (ctx.seat && ctx.seat !== cur)) return { mode: "deciding", color: null, option: false, reveal: () => {} };
  const shown = ctx.seat === cur || revealedKey === key;
  return {
    mode: shown ? "mine" : "cover",
    color: cur,
    option: !!win.options?.[cur],
    reveal: () => setRevealedKey(key),
  };
}

// Pass & Play: the browser answers No when the asked player's timer runs out (online the server does).
export function useAutoDecline({ enabled, remaining, deadlineMs, busy, onDecide }) {
  const firedFor = useRef(null);
  useEffect(() => {
    if (!enabled || !deadlineMs || busy) return;
    if (remaining <= 0 && firedFor.current !== deadlineMs) {
      firedFor.current = deadlineMs;
      onDecide(false);
    }
  }, [enabled, remaining, deadlineMs, busy, onDecide]);
}

export function CountdownBar({ remaining, total, testid }) {
  return (
    <div className="overflow-hidden shrink-0" style={{ height: 3, borderRadius: 2, background: "var(--line-soft)" }}>
      <div data-testid={testid} style={{ height: "100%", width: `${Math.min(100, (remaining / total) * 100)}%`, background: "var(--sea)", transition: "width 500ms linear" }} />
    </div>
  );
}

export const DECIDING_TEXT = "Players are deciding on responses…";

// What every other screen sees: no names, no card, no buttons.
export function DecidingBody({ remaining, size = "sm" }) {
  return (
    <div data-testid="response-deciding" className="flex-1 flex flex-col items-center justify-center text-center" style={{ gap: 6 }}>
      <div data-testid="response-deciding-text" style={{ fontSize: size === "lg" ? 22 : 14, lineHeight: size === "lg" ? "28px" : "18px", fontWeight: 700, color: "var(--ink)" }}>
        {DECIDING_TEXT} <span className="tabular-nums" data-testid="response-deciding-seconds">{remaining}s</span>
      </div>
      <div style={{ fontSize: size === "lg" ? 13 : 11.5, color: "var(--ink-3)" }}>Everyone answers privately on their own screen.</div>
    </div>
  );
}

// Pass & Play: neutral cover before a player's private panel.
export function CoverBody({ name, onReveal, size = "sm" }) {
  return (
    <div data-testid="response-cover" className="flex-1 flex flex-col items-center justify-center text-center" style={{ gap: 10 }}>
      <button
        type="button"
        data-testid="response-reveal-btn"
        onClick={onReveal}
        className={size === "lg" ? "btn-tactile btn-tactile--primary" : "btn-primary btn-compact w-full"}
        style={size === "lg" ? undefined : { height: 52, whiteSpace: "normal", lineHeight: "16px" }}
      >
        {name}: tap to see your response (others look away)
      </button>
    </div>
  );
}
